import {useGetBatchesByIds} from '@/modules/production/api/use-get-batches-by-ids'
import {useGetSettings} from '@/modules/settings/api/use-get-settings'
import {buildOrdersSolventRequirements} from '../utils'
import {ResourceDrawerSummary} from '@/shared/components/ui/resource-drawer-summary'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Drawer, Tabs, useDrawerContext} from '@chakra-ui/react'
import {DistributedBatchesListResponseSchema, DispatchMode, PatientTherapySequence} from '@cypro/engine/schema'
import {getAggregatedBatchInfo} from '@cypro/engine/utils'
import {DrawerBody, DrawerHeader} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {formatDateKey} from '@cypro/ui/utils'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {FormEvent, useCallback, useMemo, useRef} from 'react'
import {LuListChecks} from 'react-icons/lu'
import * as z from 'zod'
import {useDistributedBatchesList} from '../../../api/use-distributed-batches-list'
import {openPrintDialog} from '../../dialogs/open-print-dialog'
import {PickingListDrawerTabsList} from './picking-list-drawer-tab-list'
import {AggregatedPickListTab} from './tabs/aggregated-pick-list-tab'
import {ViewByOrderTab} from './tabs/view-by-order-tab'

const StartProtductionValidationSchema = DistributedBatchesListResponseSchema.extend({
  productionLocationId: z.string().nonempty(),
  dispatchMode: DispatchMode,
})

export type StartProductionFormValues = z.infer<typeof StartProtductionValidationSchema>

export interface PickingListDrawerProps {
  selectedOrders: PatientTherapySequence[]
  onClearSelection?: VoidFunction
}

export function PickingListDrawer({selectedOrders, onClearSelection}: PickingListDrawerProps) {
  const {t} = useTranslate('common')
  const zero = useZero()
  const {distributedBatchesList} = useDistributedBatchesList({
    therapyIds: selectedOrders.map(o => o.id),
  })

  const drawerCtx = useDrawerContext()

  // Freezes the system-generated distribution as first fetched, so it can be compared
  // against the pharmacist's final edits when the order is submitted.
  const initialOrdersRef = useRef<StartProductionFormValues['orders'] | null>(null)
  if (distributedBatchesList && initialOrdersRef.current === null) {
    initialOrdersRef.current = distributedBatchesList.orders
  }

  const form = useAppForm({
    defaultValues: {
      orders: distributedBatchesList?.orders || [],
      allUsedBatchIds: distributedBatchesList?.allUsedBatchIds || [],
      productionLocationId: '',
      dispatchMode: DispatchMode.enum.manual,
    } as StartProductionFormValues,
    validators: {
      onDynamic: StartProtductionValidationSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit({formApi}) {
      const sequenceIds = selectedOrders.map(o => o.id)
      const payload = {
        sequenceIds,
        orders: formApi.state.values.orders,
        initialOrders: initialOrdersRef.current ?? [],
        productionLocationId: formApi.state.values.productionLocationId,
        usedBatches: formApi.state.values.allUsedBatchIds,
        dispatchMode: formApi.state.values.dispatchMode,
      }
      onClearSelection?.()
      drawerCtx.setOpen(false)
      return startOrder(payload)
    },
  })

  function startOrder(payload: Parameters<typeof zero.mutators.patientsTherapies.createOrder>[0]) {
    return zero.attemptMutation(zero.mutators.patientsTherapies.createOrder(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('order_created_successfully')})
      form.reset()
      openPrintDialog(selectedOrders[0].id)
    })
  }

  const requiredBatchIds = useStore(form.store, state => {
    return state.values.allUsedBatchIds || []
  })

  const [batches, batchesState] = useGetBatchesByIds(requiredBatchIds)
  // until the batches sync, every line counts as 0 in stock; only wait while one is still missing,
  // so adding a batch later doesn't hide the whole list again
  const batchesLoading = useMemo(() => {
    if (batchesState.type === 'complete') return false
    const loadedIds = new Set((batches || []).map(b => b.id))
    return requiredBatchIds.some(id => !loadedIds.has(id))
  }, [batches, batchesState.type, requiredBatchIds])
  const orders = useStore(form.store, state => state.values.orders)

  // derived, not stored: the distributed list and the batches load after the form mounts,
  // so a snapshot taken on mount stayed empty until the first edit
  const aggregated = useMemo(() => getAggregatedBatchInfo(orders, batches || []), [orders, batches])

  const {settings} = useGetSettings()
  const skipSolventStockCheck = Boolean(settings?.skipSolventStockCheck)
  const solventRequirements = useMemo(() => buildOrdersSolventRequirements(orders, batches || []), [orders, batches])

  const mappedBatches = aggregated.mappedBatches
  const mappedRestVials = aggregated.mappedRestVials
  const mappedReturns = aggregated.mappedReturns

  const handleRemoveBatch = useCallback(
    (batchId: string, category: 'activeSubstances' | 'carrier' | 'solvents', orderIndex: number) => {
      const orders = form.getFieldValue('orders')
      for (let i = 0; i < orders.length; i++) {
        if (i !== orderIndex) continue
        const categoryBatches = orders[i][category].batches
        const filtered = categoryBatches.filter(b => b.batchId !== batchId)
        if (filtered.length !== categoryBatches.length) {
          form.setFieldValue(`orders[${i}].${category}.batches`, filtered)
        }
      }
    },
    [form]
  )

  const handleIsolateBatch = useCallback(
    (batchId: string, category: 'activeSubstances' | 'carrier' | 'solvents', orderIndex: number) => {
      const orders = form.getFieldValue('orders')
      const isolatedOrderId = orders[orderIndex].orderId
      for (let i = 0; i < orders.length; i++) {
        const categoryBatches = orders[i][category].batches
        if (i === orderIndex) {
          // Clear sharedWith on the isolated order's batch
          const updated = categoryBatches.map(b => (b.batchId === batchId ? {...b, sharedWith: []} : b))
          form.setFieldValue(`orders[${i}].${category}.batches`, updated)
        } else {
          // Remove the isolated order's id from sharedWith; clear if only 1 left
          const updated = categoryBatches.map(b => {
            if (b.batchId !== batchId) return b
            const remaining = b.sharedWith.filter(id => id !== isolatedOrderId)
            return {...b, sharedWith: remaining.length <= 1 ? [] : remaining}
          })
          form.setFieldValue(`orders[${i}].${category}.batches`, updated)
        }
      }
    },
    [form]
  )

  function addBatch(batchId: string) {
    form.pushFieldValue('allUsedBatchIds', batchId)
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (batchesLoading) return
    const orders = form.getFieldValue('orders')
    const allValid = orders.every(o => o.isValid)
    if (!allValid) {
      toaster.error({title: t('error'), description: t('orders_have_stock_issues')})
      return
    }
    form.handleSubmit(event)
  }

  return (
    <>
      <DrawerHeader>
        <Drawer.Title>{t('generate_picking_list')}</Drawer.Title>
      </DrawerHeader>

      <DrawerBody>
        <ResourceDrawerSummary
          title={t('generate_picking_list')}
          description={formatDateKey(new Date().getTime())}
          icon={LuListChecks}
        />
        <Tabs.Root defaultValue="orders" spaceY="3" lazyMount>
          <PickingListDrawerTabsList />
          {distributedBatchesList && (
            <>
              <AggregatedPickListTab
                mappedBatches={mappedBatches}
                mappedRestVials={mappedRestVials}
                mappedReturns={mappedReturns}
                solventRequirements={skipSolventStockCheck ? solventRequirements : undefined}
              />
              <ViewByOrderTab
                selectedOrders={selectedOrders}
                batches={batches || []}
                batchesLoading={batchesLoading}
                addBatch={addBatch}
                handleRemoveBatch={handleRemoveBatch}
                handleIsolateBatch={handleIsolateBatch}
                batchErrors={aggregated.errors}
                onSubmit={onSubmit}
                form={form}
              />
            </>
          )}
        </Tabs.Root>
      </DrawerBody>
    </>
  )
}
